<template>
    <algolia-places-get
        :per-page="1"
        :preloaded="store.places"
        :search-on-created="store.places ? null : form.location"
        @success="onPlacesGet"
        v-slot="{
            data: hits,
            search
        }"
    >
        <with-variables
            :variables="{ place: hits[0] }"
            v-slot="{
                place
            }"
        >
            <algolia-get
                index="listings"
                :page="page"
                :per-page="25"
                :preloaded="store.listings"
                :get-on-created="false"
                :facets="['property_type']"
                :disjunctive-facets="['property_type']"
                class="container max-w-2xl mx-auto"
                v-slot="{
                    data: listings,
                    meta: listings_meta,
                    clear,
                    filterByLatlng,
                    filterByRadius,
                    filterByNumeric,
                    filterByBoolean,
                    filterByDisjunctiveFacet,
                    get
                }"
            >
                <with-variables
                    ref="withVariables"
                    :variables="{
                        getListings: () => {
                            clear()
                            filterByLatlng(latLngByPlace(place))
                            filterByRadius(parseInt(form.radius * 1609, 0))
                            _.each(form.property_type, value => filterByDisjunctiveFacet('property_type', value))
                            if (form.rent_minimum > 0) filterByNumeric('rent_amount', '>=', form.rent_minimum * 100)
                            if (form.rent_maximum > 0) filterByNumeric('rent_amount', '<=', form.rent_maximum * 100)
                            if (form.bedrooms_minimum > 0) filterByNumeric('number_of_bedrooms', '>=', form.bedrooms_minimum)
                            if (form.bedrooms_maximum > 0) filterByNumeric('number_of_bedrooms', '<=', form.bedrooms_maximum)
                            if (form.is_furnished >= 0) filterByBoolean('is_furnished', form.is_furnished)
                            if (form.has_parking) filterByBoolean('has_parking', form.has_parking)
                            if (form.has_garden) filterByBoolean('has_garden', form.has_garden)
                            if (form.allows_pets) filterByBoolean('allows_pets', form.allows_pets)
                            if (form.allows_smoking) filterByBoolean('allows_smoking', form.allows_smoking)
                            if (form.allows_dss) filterByBoolean('allows_dss', form.allows_dss)
                            get()
                        }
                    }"
                    v-slot="{
                        getListings
                    }"
                >
                    <div>
                        <grid-layout>
                            <grid-layout-item class="lg:w-8/12">
                                <div class="flex items-center py-2 pl-6 pr-4 mb-3 rounded bg-grey-lightest">
                                    <base-svg
                                        name="icon-location-pin"
                                        class="flex-none w-6 h-6"
                                    />

                                    <input-text
                                        v-model="form.location"
                                        name="location"
                                        type="text"
                                        placeholder="Location"
                                        class="bg-transparent border-0 focus:shadow-none"
                                        @input="search(form.location, getListings)"
                                    />

                                    <input-select
                                        v-model="form.radius"
                                        placeholder="Radius"
                                        :options="[
                                            { value: 0, label: 'Auto-radius' },
                                            { value: 0.25, label: '1/4 mile' },
                                            { value: 0.5, label: '1/2 mile' },
                                            { value: 1, label: '1 mile' },
                                            { value: 3, label: '3 miles' },
                                            { value: 5, label: '5 miles' },
                                            { value: 10, label: '10 miles' },
                                            { value: 15, label: '15 miles' },
                                            { value: 20, label: '20 miles' },
                                            { value: 30, label: '30 miles' },
                                            { value: 40, label: '40 miles' },
                                        ]"
                                        name="radius"
                                        @input="getListings"
                                        class="flex-none hidden md:inline"
                                        input-class="border-0"
                                    />
                                </div>

                                <div class="flex mb-8 md:mb-12">
                                    <div class="flex flex-grow">
                                        <base-dropdown
                                            placement="bottom-start"
                                            class="hidden mr-3 md:block"
                                            :exclude="[
                                                '.rent-minimum-dropdown-menu',
                                                '.rent-maximum-dropdown-menu'
                                            ]"
                                        >
                                            <button
                                                slot="trigger"
                                                slot-scope="{ is_open, toggle }"
                                                type="button"
                                                @click.prevent="toggle"
                                                :class="{ 'is-active': is_open }"
                                                class="flex items-center px-4 pt-3 pb-2 border rounded"
                                            >
                                                Price

                                                <base-svg
                                                    name="icon-cheveron-down"
                                                    class="w-6 h-6 ml-1"
                                                />
                                            </button>

                                            <div
                                                slot="menu"
                                                class="w-64 p-4 rounded shadow-lg bg-grey-darker mt-3px"
                                            >
                                                <form-item
                                                    sid="~"
                                                    name="rent_minimum"
                                                >
                                                    <span
                                                        slot="label"
                                                        class="text-grey-light"
                                                    >
                                                        Min Price
                                                    </span>

                                                    <input-select
                                                        v-model="form.rent_minimum"
                                                        placeholder="Price Minimum"
                                                        :options="[
                                                            { value: -1, label: 'No min price' },
                                                            { value: 100, label: '£100pcm' },
                                                            { value: 200, label: '£200pcm' },
                                                            { value: 300, label: '£300pcm' },
                                                            { value: 400, label: '£400pcm' },
                                                            { value: 500, label: '£500pcm' },
                                                            { value: 600, label: '£600pcm' },
                                                            { value: 700, label: '£700pcm' },
                                                            { value: 800, label: '£800pcm' },
                                                            { value: 900, label: '£900pcm' },
                                                            { value: 1000, label: '£100pcm' }
                                                        ]"
                                                        name="rent_minimum"
                                                        :is-small="true"
                                                        :is-dark="true"
                                                        @input="getListings"
                                                    />
                                                </form-item>

                                                <form-item
                                                    sid="~"
                                                    name="rent_maximum"
                                                    class="mb-0!"
                                                >
                                                    <span
                                                        slot="label"
                                                        class="text-grey-light"
                                                    >
                                                        Max Price
                                                    </span>

                                                    <input-select
                                                        v-model="form.rent_maximum"
                                                        placeholder="Price Maximum"
                                                        :options="[
                                                            { value: -1, label: 'No max rent' },
                                                            { value: 100, label: '£100pcm' },
                                                            { value: 200, label: '£200pcm' },
                                                            { value: 300, label: '£300pcm' },
                                                            { value: 400, label: '£400pcm' },
                                                            { value: 500, label: '£500pcm' },
                                                            { value: 600, label: '£600pcm' },
                                                            { value: 700, label: '£700pcm' },
                                                            { value: 800, label: '£800pcm' },
                                                            { value: 900, label: '£900pcm' },
                                                            { value: 1000, label: '£100pcm' }
                                                        ]"
                                                        :is-small="true"
                                                        :is-dark="true"
                                                        name="rent_maximum"
                                                        @input="getListings"
                                                    />
                                                </form-item>
                                            </div>
                                        </base-dropdown>

                                        <base-dropdown
                                            placement="bottom-start"
                                            class="hidden mr-3 md:block"
                                            :exclude="[
                                                '.bedrooms-minimum-dropdown-menu',
                                                '.bedrooms-maximum-dropdown-menu'
                                            ]"
                                        >
                                            <button
                                                slot="trigger"
                                                slot-scope="{ is_open, toggle }"
                                                type="button"
                                                @click.prevent="toggle"
                                                :class="{ 'is-active': is_open }"
                                                class="flex items-center px-4 pt-3 pb-2 border rounded"
                                            >
                                                Beds

                                                <base-svg
                                                    name="icon-cheveron-down"
                                                    class="w-6 h-6 ml-1"
                                                />
                                            </button>

                                            <div
                                                slot="menu"
                                                class="w-64 p-4 rounded shadow-lg bg-grey-darker mt-3px"
                                            >
                                                <form-item
                                                    sid="~"
                                                    name="bedrooms_minimum"
                                                >
                                                    <span
                                                        slot="label"
                                                        class="text-grey-light"
                                                    >
                                                        Min Bedrooms
                                                    </span>

                                                    <input-select
                                                        v-model="form.bedrooms_minimum"
                                                        placeholder="Bedrooms Minimum"
                                                        :options="[
                                                            { value: -1, label: 'No min bedrooms' },
                                                            { value: 1, label: '1' },
                                                            { value: 2, label: '2' },
                                                            { value: 3, label: '3' },
                                                            { value: 4, label: '4' },
                                                            { value: 5, label: '5' },
                                                            { value: 6, label: '6' },
                                                            { value: 7, label: '7' },
                                                            { value: 8, label: '8' },
                                                            { value: 9, label: '9' },
                                                            { value: 10, label: '10' }
                                                        ]"
                                                        :is-small="true"
                                                        :is-dark="true"
                                                        name="bedrooms_minimum"
                                                        @input="getListings"
                                                    />
                                                </form-item>

                                                <form-item
                                                    sid="~"
                                                    name="bedrooms_maximum"
                                                    class="mb-0!"
                                                >
                                                    <span
                                                        slot="label"
                                                        class="text-grey-light"
                                                    >
                                                        Max Bedrooms
                                                    </span>

                                                    <input-select
                                                        v-model="form.bedrooms_maximum"
                                                        placeholder="Bedrooms Maximum"
                                                        :options="[
                                                            { value: -1, label: 'No max bedrooms' },
                                                            { value: 1, label: '1' },
                                                            { value: 2, label: '2' },
                                                            { value: 3, label: '3' },
                                                            { value: 4, label: '4' },
                                                            { value: 5, label: '5' },
                                                            { value: 6, label: '6' },
                                                            { value: 7, label: '7' },
                                                            { value: 8, label: '8' },
                                                            { value: 9, label: '9' },
                                                            { value: 10, label: '10' }
                                                        ]"
                                                        :is-small="true"
                                                        :is-dark="true"
                                                        name="bedrooms_maximum"
                                                        @input="getListings"
                                                    />
                                                </form-item>
                                            </div>
                                        </base-dropdown>

                                        <base-dropdown
                                            placement="bottom-start"
                                            class="hidden mr-3 md:block"
                                        >
                                            <button
                                                slot="trigger"
                                                slot-scope="{ is_open, toggle }"
                                                type="button"
                                                @click.prevent="toggle"
                                                :class="{ 'is-active': is_open }"
                                                class="flex items-center px-4 pt-3 pb-2 border rounded"
                                            >
                                                Property

                                                <base-svg
                                                    name="icon-cheveron-down"
                                                    class="w-6 h-6 ml-1"
                                                />
                                            </button>

                                            <div
                                                slot="menu"
                                                class="p-4 rounded shadow-lg bg-grey-darker mt-3px w-80"
                                            >
                                                <form-item
                                                    sid="~"
                                                    name="~"
                                                    class="text-sm text-white"
                                                >
                                                    <span
                                                        slot="label"
                                                        class="text-grey-light"
                                                    >
                                                        Type
                                                    </span>

                                                    <grid-layout>
                                                        <grid-layout-item
                                                            v-for="(label, value) in {
                                                                house: 'House',
                                                                flat: 'Flat',
                                                                apartment: 'Apartment',
                                                                bungalow: 'Bungalow',
                                                                land: 'Land',
                                                                commercial: 'Commercial',
                                                                other: 'Other'
                                                            }"
                                                            :key="value"
                                                            class="w-1/2 mt-2"
                                                        >
                                                            <label class="flex items-center cursor-pointer">
                                                                <input-checkbox
                                                                    v-model="form.property_type"
                                                                    :value="value"
                                                                    class="mr-3 border-grey-dark!"
                                                                    @input="getListings"
                                                                />

                                                                <div class="leading-none">{{ label }}</div>
                                                            </label>
                                                        </grid-layout-item>
                                                    </grid-layout>
                                                </form-item>
                                            </div>
                                        </base-dropdown>

                                        <base-dropdown
                                            placement="bottom-start"
                                            class="mr-3"
                                            :exclude="[
                                                '.rent-minimum-dropdown-menu',
                                                '.rent-maximum-dropdown-menu',
                                                '.bedrooms-minimum-dropdown-menu',
                                                '.bedrooms-maximum-dropdown-menu',
                                                '.is-furnished-dropdown-menu'
                                            ]"
                                        >
                                            <button
                                                slot="trigger"
                                                slot-scope="{ is_open, toggle }"
                                                type="button"
                                                @click.prevent="toggle"
                                                :class="{ 'is-active': is_open }"
                                                class="flex items-center px-4 pt-3 pb-2 border rounded"
                                            >
                                                <span class="hidden md:inline">More filters</span>
                                                <span class="md:hidden">Filters</span>

                                                <base-svg
                                                    name="icon-cheveron-down"
                                                    class="w-6 h-6 ml-1"
                                                />
                                            </button>

                                            <div
                                                slot="menu"
                                                class="p-4 overflow-x-scroll rounded shadow-lg bg-grey-darker max-h-xs scrollbar-hidden mt-3px w-72"
                                            >
                                                <div class="md:hidden">
                                                    <form-item
                                                        sid="~"
                                                        name="rent_minimum"
                                                    >
                                                        <span
                                                            slot="label"
                                                            class="text-grey-light"
                                                        >
                                                            Min Price
                                                        </span>

                                                        <input-select
                                                            v-model="form.rent_minimum"
                                                            placeholder="Price Minimum"
                                                            :options="[
                                                                { value: -1, label: 'No min price' },
                                                                { value: 100, label: '£100pcm' },
                                                                { value: 200, label: '£200pcm' },
                                                                { value: 300, label: '£300pcm' },
                                                                { value: 400, label: '£400pcm' },
                                                                { value: 500, label: '£500pcm' },
                                                                { value: 600, label: '£600pcm' },
                                                                { value: 700, label: '£700pcm' },
                                                                { value: 800, label: '£800pcm' },
                                                                { value: 900, label: '£900pcm' },
                                                                { value: 1000, label: '£100pcm' }
                                                            ]"
                                                            name="rent_minimum"
                                                            :is-small="true"
                                                            :is-dark="true"
                                                            @input="getListings"
                                                        />
                                                    </form-item>

                                                    <form-item
                                                        sid="~"
                                                        name="rent_maximum"
                                                    >
                                                        <span
                                                            slot="label"
                                                            class="text-grey-light"
                                                        >
                                                            Max Price
                                                        </span>

                                                        <input-select
                                                            v-model="form.rent_maximum"
                                                            placeholder="Price Maximum"
                                                            :options="[
                                                                { value: -1, label: 'No max rent' },
                                                                { value: 100, label: '£100pcm' },
                                                                { value: 200, label: '£200pcm' },
                                                                { value: 300, label: '£300pcm' },
                                                                { value: 400, label: '£400pcm' },
                                                                { value: 500, label: '£500pcm' },
                                                                { value: 600, label: '£600pcm' },
                                                                { value: 700, label: '£700pcm' },
                                                                { value: 800, label: '£800pcm' },
                                                                { value: 900, label: '£900pcm' },
                                                                { value: 1000, label: '£100pcm' }
                                                            ]"
                                                            :is-small="true"
                                                            :is-dark="true"
                                                            name="rent_maximum"
                                                            @input="getListings"
                                                        />
                                                    </form-item>

                                                    <form-item
                                                        sid="~"
                                                        name="bedrooms_minimum"
                                                    >
                                                        <span
                                                            slot="label"
                                                            class="text-grey-light"
                                                        >
                                                            Min Bedrooms
                                                        </span>

                                                        <input-select
                                                            v-model="form.bedrooms_minimum"
                                                            placeholder="Bedrooms Minimum"
                                                            :options="[
                                                                { value: -1, label: 'No min bedrooms' },
                                                                { value: 1, label: '1' },
                                                                { value: 2, label: '2' },
                                                                { value: 3, label: '3' },
                                                                { value: 4, label: '4' },
                                                                { value: 5, label: '5' },
                                                                { value: 6, label: '6' },
                                                                { value: 7, label: '7' },
                                                                { value: 8, label: '8' },
                                                                { value: 9, label: '9' },
                                                                { value: 10, label: '10' }
                                                            ]"
                                                            :is-small="true"
                                                            :is-dark="true"
                                                            name="bedrooms_minimum"
                                                            @input="getListings"
                                                        />
                                                    </form-item>

                                                    <form-item
                                                        sid="~"
                                                        name="bedrooms_maximum"
                                                    >
                                                        <span
                                                            slot="label"
                                                            class="text-grey-light"
                                                        >
                                                            Max Bedrooms
                                                        </span>

                                                        <input-select
                                                            v-model="form.bedrooms_maximum"
                                                            placeholder="Bedrooms Maximum"
                                                            :options="[
                                                                { value: -1, label: 'No max bedrooms' },
                                                                { value: 1, label: '1' },
                                                                { value: 2, label: '2' },
                                                                { value: 3, label: '3' },
                                                                { value: 4, label: '4' },
                                                                { value: 5, label: '5' },
                                                                { value: 6, label: '6' },
                                                                { value: 7, label: '7' },
                                                                { value: 8, label: '8' },
                                                                { value: 9, label: '9' },
                                                                { value: 10, label: '10' }
                                                            ]"
                                                            :is-small="true"
                                                            :is-dark="true"
                                                            name="bedrooms_maximum"
                                                            @input="getListings"
                                                        />
                                                    </form-item>

                                                    <form-item
                                                        sid="~"
                                                        name="~"
                                                        class="text-sm text-white"
                                                    >
                                                        <span
                                                            slot="label"
                                                            class="text-grey-light"
                                                        >
                                                            Type
                                                        </span>

                                                        <grid-layout>
                                                            <grid-layout-item
                                                                v-for="(label, value) in {
                                                                    house: 'House',
                                                                    flat: 'Flat',
                                                                    apartment: 'Apartment',
                                                                    bungalow: 'Bungalow',
                                                                    land: 'Land',
                                                                    commercial: 'Commercial',
                                                                    other: 'Other'
                                                                }"
                                                                :key="value"
                                                                class="w-1/2 mt-2"
                                                            >
                                                                <label class="flex items-center cursor-pointer">
                                                                    <input-checkbox
                                                                        v-model="form.property_type"
                                                                        :value="value"
                                                                        class="mr-3 border-grey-dark!"
                                                                        @input="getListings"
                                                                    />

                                                                    <div class="leading-none">{{ label }}</div>
                                                                </label>
                                                            </grid-layout-item>
                                                        </grid-layout>
                                                    </form-item>
                                                </div>

                                                <form-item
                                                    sid="~"
                                                    name="is_furnished"
                                                >
                                                    <span
                                                        slot="label"
                                                        class="text-grey-light"
                                                    >
                                                        Furnishings
                                                    </span>

                                                    <input-select
                                                        v-model="form.is_furnished"
                                                        placeholder="Furnishings"
                                                        :options="[
                                                            { value: -1, label: 'Furnished and unfurnished' },
                                                            { value: 1, label: 'Furnished' },
                                                            { value: 0, label: 'Unfurnished' }
                                                        ]"
                                                        :is-small="true"
                                                        :is-dark="true"
                                                        name="is_furnished"
                                                        @input="getListings"
                                                    />
                                                </form-item>

                                                <form-item
                                                    sid="~"
                                                    name="~"
                                                    class="text-white text-sm mb-0!"
                                                >
                                                    <span
                                                        slot="label"
                                                        class="text-grey-light"
                                                    >
                                                        Must have/allow
                                                    </span>

                                                    <grid-layout>
                                                        <grid-layout-item class="w-1/2 mt-2">
                                                            <label class="flex items-center cursor-pointer">
                                                                <input-checkbox
                                                                    v-model="form.has_parking"
                                                                    class="mr-3 border-grey-dark!"
                                                                    @input="getListings"
                                                                />

                                                                <div class="leading-none">Parking</div>
                                                            </label>
                                                        </grid-layout-item>

                                                        <grid-layout-item class="w-1/2 mt-2">
                                                            <label class="flex items-center cursor-pointer">
                                                                <input-checkbox
                                                                    v-model="form.allows_pets"
                                                                    class="mr-3 border-grey-dark!"
                                                                    @input="getListings"
                                                                />

                                                                <div class="leading-none">Pets</div>
                                                            </label>
                                                        </grid-layout-item>

                                                        <grid-layout-item class="w-1/2 mt-2">
                                                            <label class="flex items-center cursor-pointer">
                                                                <input-checkbox
                                                                    v-model="form.allows_dss"
                                                                    class="mr-3 border-grey-dark!"
                                                                    @input="getListings"
                                                                />

                                                                <div class="leading-none">DSS</div>
                                                            </label>
                                                        </grid-layout-item>

                                                        <grid-layout-item class="w-1/2 mt-2">
                                                            <label class="flex items-center cursor-pointer">
                                                                <input-checkbox
                                                                    v-model="form.allows_smoking"
                                                                    class="mr-3 border-grey-dark!"
                                                                    @input="getListings"
                                                                />

                                                                <div class="leading-none">Smoking</div>
                                                            </label>
                                                        </grid-layout-item>

                                                        <grid-layout-item class="w-1/2 mt-2">
                                                            <label class="flex items-center cursor-pointer">
                                                                <input-checkbox
                                                                    v-model="form.has_garden"
                                                                    class="mr-3 border-grey-dark!"
                                                                    @input="getListings"
                                                                />

                                                                <div class="leading-none">Garden</div>
                                                            </label>
                                                        </grid-layout-item>
                                                    </grid-layout>
                                                </form-item>
                                            </div>
                                        </base-dropdown>
                                    </div>

                                    <input-select
                                        v-model="form.radius"
                                        placeholder="Radius"
                                        :options="[
                                            { value: 0, label: 'Auto-radius' },
                                            { value: 0.25, label: '1/4 mile' },
                                            { value: 0.5, label: '1/2 mile' },
                                            { value: 1, label: '1 mile' },
                                            { value: 3, label: '3 miles' },
                                            { value: 5, label: '5 miles' },
                                            { value: 10, label: '10 miles' },
                                            { value: 15, label: '15 miles' },
                                            { value: 20, label: '20 miles' },
                                            { value: 30, label: '30 miles' },
                                            { value: 40, label: '40 miles' },
                                        ]"
                                        name="radius"
                                        @input="getListings"
                                        class="flex-none md:hidden"
                                    />
                                </div>

                                <div class="mb-6">
                                    <h2 class="mb-2 text-2xl font-semibold md:text-4xl font-noto-sans text-grey-darkest">
                                        Properties to rent in
                                        <span class="text-blue">{{ place_array | join(', ') }}</span>
                                    </h2>

                                    <h3 class="font-semibold text-label">{{ listings_meta.total }} Result<template v-if="listings_meta.total != 1">s</template></h3>
                                </div>
                            </grid-layout-item>
                        </grid-layout>

                        <grid-layout class="mb-24">
                            <grid-layout-item class="mb-6 lg:w-8/12 lg:mb-0">
                                <div
                                    v-for="(listing, index) in listings"
                                    :key="listing.uuid"
                                >
                                    <rentora-ad
                                        v-if="index === 3 && listings.length > 4"
                                        :is-small="true"
                                        class="mb-6 lg:hidden"
                                    />

                                    <router-link
                                        :to="{
                                            name: 'listings.show',
                                            params: {
                                                listing: listing.uuid
                                            }
                                        }"
                                        class="flex flex-wrap mb-6 border rounded shadow border-grey-lightest"
                                    >
                                        <div class="block w-full h-48 md:w-2/5 md:h-auto">
                                            <div
                                                :style="{
                                                    'background-image': 'url(' + $laroute.route('storage.files.show', {
                                                        file: listing.images[0].uuid
                                                    }) + ')'
                                                }"
                                                class="h-full bg-center bg-cover rounded-t bg-grey-bg bg-grey-lightest md:rounded-bl md:rounded-tr-none"
                                            />
                                        </div>

                                        <div class="w-full md:w-3/5">
                                            <div class="px-4 py-3 border-b-2 rounded rounded-l-none md:px-6 border-grey-lightest">
                                                <div class="flex items-center justify-between mb-1 font-bold text-label text-grey-dark">
                                                    <div>
                                                        {{ listing.number_of_bedrooms }} Bed {{ listing.property_type }}
                                                    </div>

                                                    <div
                                                        v-if="place"
                                                        class="flex-none lowercase"
                                                    >
                                                        {{ distanceBetweenLatLngs(listing.address.latitude, listing.address.longitude, place._geoloc.lat, place._geoloc.lng) | round(1) }} mi
                                                    </div>
                                                </div>

                                                <div class="mb-0 text-3xl leading-tight sm:text-4xl text-grey-darkest">
                                                    {{ listing.rent_amount | money(0) }}<!--
                                                    --><span class="text-xl text-grey-dark"> / month</span>
                                                </div>

                                                <div class="mb-2 text-sm font-semibold md:text-base text-grey-dark">
                                                    + {{ listing.deposit_amount | money(0) }} deposit
                                                </div>

                                                <div class="text-base font-semibold">
                                                    <base-address
                                                        :address="listing.address"
                                                        :is-inline="true"
                                                        class="inline text-grey-darker"
                                                    />
                                                </div>
                                            </div>

                                            <div class="flex justify-between px-4 py-4 text-xs md:px-8 text-grey-darker lg:text-sm">
                                                <div class="inline-flex items-center">
                                                    <base-svg
                                                        name="icon-sofa"
                                                        class="w-5 mr-2 sm:w-6"
                                                    />

                                                    <span class="font-bold text-grey-darkest">{{ listing.number_of_receptions }}</span>
                                                    &nbsp;
                                                    <span class="xs:hidden lg:inline xl:hidden">
                                                        Rcpt.
                                                    </span>
                                                    <span class="hidden xs:inline lg:hidden xl:inline">
                                                        Receptions
                                                    </span>
                                                </div>

                                                <div class="inline-flex items-center">
                                                    <base-svg
                                                        name="icon-bed"
                                                        class="w-5 mr-2 sm:w-6"
                                                    />

                                                    <span class="font-bold text-grey-darkest">{{ listing.number_of_bedrooms }}</span>
                                                    &nbsp;
                                                    <span class="xs:hidden lg:inline xl:hidden">
                                                        Bdrm.
                                                    </span>
                                                    <span class="hidden xs:inline lg:hidden xl:inline">
                                                        Bedrooms
                                                    </span>
                                                </div>

                                                <div class="inline-flex items-center">
                                                    <base-svg
                                                        name="icon-bath"
                                                        class="w-5 mr-2 sm:w-6"
                                                    />

                                                    <span class="font-bold text-grey-darkest">{{ listing.number_of_bathrooms }}</span>
                                                    &nbsp;
                                                    <span class="xs:hidden lg:inline xl:hidden">
                                                        Bthr.
                                                    </span>
                                                    <span class="hidden xs:inline lg:hidden xl:inline">
                                                        Bathrooms
                                                    </span>
                                                </div>
                                            </div>

                                            <div class="px-4 py-4 text-xs font-semibold leading-none md:px-6 bg-grey-lightest text-label xs:text-sm">
                                                <span class="xs:hidden">
                                                    Avl.
                                                </span>
                                                <span class="hidden xs:inline">
                                                    Available
                                                </span>
                                                <template v-if="listing.available_at && $moment(listing.available_at).diff($moment()) > 0">{{ listing.available_at | moment('D MMM YYYY') }}</template>
                                                <template v-else>Now</template>
                                            </div>
                                        </div>
                                    </router-link>
                                </div>

                                <footer class="px-4 mb-6 text-sm font-semibold rounded-b bg-grey-bg sm:px-6 text-grey-darker">
                                    <base-pagination
                                        v-model="page"
                                        :per-page="listings_meta.per_page"
                                        :from="listings_meta.from"
                                        :to="listings_meta.to"
                                        :last-page="listings_meta.last_page"
                                        :count="listings_meta.count"
                                        :total="listings_meta.total"
                                        @update="getListings"
                                    />
                                </footer>
                            </grid-layout-item>

                            <grid-layout-item class="lg:w-4/12 lg:pl-6">
                                <rentora-ad />
                            </grid-layout-item>
                        </grid-layout>
                    </div>
                </with-variables>
            </algolia-get>
        </with-variables>
    </algolia-places-get>
</template>

<script>
    import _ from 'lodash'
    import HeadMixin from '../../../mixins/head'
    import PaginationMixin from '../../../mixins/pagination'
    import PreloadedMixin from '../../../mixins/preloaded'
    import RentoraAd from './index/rentora-ad'

    export default {
        mixins: [
            HeadMixin,
            PaginationMixin,
            PreloadedMixin
        ],
        components: {
            RentoraAd
        },
        data () {
            return {
                form: {
                    location: this.$route.params.location,
                    radius: _.round(_.get(this.$route.query, 'radius', 0), 2),
                    bedrooms_minimum: _.round(_.get(this.$route.query, 'minbeds', -1), 0),
                    bedrooms_maximum: _.round(_.get(this.$route.query, 'maxbeds', -1), 0),
                    rent_minimum: _.round(_.get(this.$route.query, 'minprice', -1), 0),
                    rent_maximum: _.round(_.get(this.$route.query, 'maxprice', -1), 0),
                    property_type: _.filter(_.split(_.get(this.$route.query, 'property'), ',')),
                    has_parking: !!this.$route.query.parking,
                    has_garden: !!this.$route.query.garden,
                    allows_pets: !!this.$route.query.pets,
                    allows_dss: !!this.$route.query.dss,
                    allows_smoking: !!this.$route.query.smoking,
                    is_furnished: -1
                },
                store: {
                    places: _.preload_pull('places'),
                    listings: _.preload_pull('listings')
                }
            }
        },
        computed: {
            place_array () {
                const place = _.get(this.store.places, 'hits.0')

                const array = []

                if (place) {
                    if (place.locale_names) {
                        array.push(_.first(place.locale_names))
                    }

                    if (place.city) {
                        array.push(_.first(place.city))
                    }

                    if (place.county) {
                        array.push(_.first(place.county))
                    } else if (place.administrative) {
                        array.push(_.last(place.administrative))
                    }
                }

                return array
            }
        },
        watch: {
            form: {
                handler () {
                    const query = {}

                    if (this.form.radius > 0) query.radius = this.form.radius
                    if (this.form.rent_minimum > -1) query.minprice = this.form.rent_minimum
                    if (this.form.rent_maximum > -1) query.maxprice = this.form.rent_maximum
                    if (this.form.bedrooms_minimum > -1) query.minbeds = this.form.bedrooms_minimum
                    if (this.form.bedrooms_maximum > -1) query.maxbeds = this.form.bedrooms_maximum
                    if (this.form.property_type.length) query.property = this.form.property_type.join(',')
                    if (this.form.has_parking) query.parking = '✓'
                    if (this.form.has_garden) query.garden = '✓'
                    if (this.form.allows_pets) query.pets = '✓'
                    if (this.form.allows_dss) query.dss = '✓'
                    if (this.form.allows_smoking) query.smoking = '✓'

                    this.$router.replace({
                        name: 'listings.in.index',
                        params: {
                            location: this.form.location
                        },
                        query
                    })
                },
                deep: true
            }
        },
        methods: {
            head () {
                const place = _.get(this.place_array, 0)

                return {
                    title: [
                        'Properties to rent' + (place ? ` in ${place}` : ''),
                        'Houses and flats to rent' + (place ? ` in ${place}` : '')
                    ]
                }
            },
            onPlacesGet (places, isPreload) {
                this.store.places = places

                setTimeout(() => {
                    if (!isPreload && this.$refs.withVariables) {
                        this.$refs.withVariables.variables.getListings()
                    }
                }, 0)
            },
            latLngByPlace (place) {
                return _.get(place, '_geoloc')
            },
            // @see https://stackoverflow.com/a/21623206
            distanceBetweenLatLngs (lat1, lon1, lat2, lon2) {
                var p = 0.017453292519943295 // Math.PI / 180
                var c = Math.cos
                var a = 0.5 - c((lat2 - lat1) * p) / 2 + c(lat1 * p) * c(lat2 * p) * (1 - c((lon2 - lon1) * p)) / 2

                return 7918 * Math.asin(Math.sqrt(a))
            }
        }
    }
</script>
